import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useDropzone } from "react-dropzone";
import useSettings from "../../../../hooks/settings";
import { PrinterTypes } from "../../../../models/printer_model";
import { Settings } from "../../../../models/settings_model";
import { IconButton } from "../../common/buttons";
import {
  GET_PRINTERS,
  ADD_PRINTER,
  UPDATE_PRINTER,
  DELETE_PRINTER,
} from "../../../../hooks/queries";
import { TICKET_LOGO_UPLOAD } from "../queries";
import { processImageForTicket } from "./processTicketImage";
import { has_access } from "../../../../middleware/privileges";

const Tickets: React.FC = () => {
  const [settings, saveSettings] = useSettings();
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    return () => {
      if (updated) {
        window.location.reload();
      }
    };
  }, [updated]);

  const updateSetting = async (ev: { currentTarget: HTMLButtonElement }) => {
    const setting_type = ev.currentTarget.id as keyof Settings;
    const checked = !settings[setting_type];
    await saveSettings(setting_type, checked);
    setUpdated(true);
  };

  const updatePrinterType = (ev: { currentTarget: HTMLButtonElement }) => {
    const printer_type = ev.currentTarget.id as PrinterTypes;
    debugger;
    saveSettings("printer_type", printer_type);
    setUpdated(true);
  };

  const canEditInfo = has_access(128); // edit_info

  return (
    <div className="tickets_section">
      <div className="row">
        <div className={canEditInfo ? "col-md-7" : "col-12"}>
          <PrinterSection
            settings={settings}
            updateSetting={updateSetting}
            updatePrinterType={updatePrinterType}
          />
          {settings.printer_type === "network" && (
            <NetworkPrinters branchId={settings.active_branch} />
          )}
        </div>
        {canEditInfo && (
          <div className="col">
            <RightSide />
          </div>
        )}
      </div>
    </div>
  );
};
type PrinterSectionProps = {
  settings: Settings;
  updateSetting: any;
  updatePrinterType: any;
};
const PrinterSection: React.FC<PrinterSectionProps> = ({
  settings,
  updateSetting,
  updatePrinterType,
}) => (
  <div className="card">
    <div className="card-body ">
      <h3 className="">Imprimir Tickets</h3>
      <IconButton
        clickFun={updateSetting}
        text="Imprimir tickets"
        checked={settings.price_ticket ? "true" : undefined}
        id="price_ticket"
      />
      <hr />
      <h5 className="mb-1">Tipo de Impresora</h5>
      <IconButton
        clickFun={updatePrinterType}
        text="USB"
        icon="fab fa-usb"
        classes="btn-sm"
        checked={settings.printer_type === "usb" ? "true" : undefined}
        id="usb"
      />
      <IconButton
        clickFun={updatePrinterType}
        text="Bluetooth"
        icon="fab fa-bluetooth-b"
        classes="btn-sm"
        checked={settings.printer_type === "bluetooth" ? "true" : undefined}
        id="bluetooth"
      />
      <IconButton
        clickFun={updatePrinterType}
        text="Por Red"
        icon="fas fa-project-diagram"
        classes="btn-sm"
        checked={settings.printer_type === "network" ? "true" : undefined}
        id="network"
      />
      <IconButton
        clickFun={updatePrinterType}
        text="Cloud Printing"
        icon="fas fa-cloud"
        classes="btn-sm"
        checked={settings.printer_type === "mqtt" ? "true" : undefined}
        id="mqtt"
      />
      <hr />
      <h5 className="mb-1">Opciones</h5>
      <IconButton
        clickFun={updateSetting}
        text="Imprimir ticket cliente al hacer pedido"
        classes="btn-sm"
        checked={settings.print_upfront ? "true" : undefined}
        id="print_upfront"
      />
      <IconButton
        clickFun={updateSetting}
        text="Imprimir Copia Restaurant al hacer pedido"
        classes="btn-sm"
        checked={settings.restaurant_copy ? "true" : undefined}
        id="restaurant_copy"
      />
      <IconButton
        clickFun={updateSetting}
        text="Abrir Caja de Monedas al Cobrar"
        classes="btn-sm"
        checked={settings.open_drawer ? "true" : undefined}
        id="open_drawer"
      />
      <IconButton
        clickFun={updateSetting}
        text="Información Fiscal en Ticket"
        classes="btn-sm"
        checked={settings.printer_fiscal ? "true" : undefined}
        id="printer_fiscal"
      />
      <IconButton
        clickFun={updateSetting}
        text="Imprimir IVA en Tickets"
        classes="btn-sm"
        checked={settings.printer_iva ? "true" : undefined}
        id="printer_iva"
      />
      <IconButton
        clickFun={updateSetting}
        text="Agregar Propina Sugerida en Tickets (15%)"
        classes="btn-sm"
        checked={settings.suggested_tip ? "true" : undefined}
        id="suggested_tip"
      />
      <IconButton
        clickFun={updateSetting}
        text="Imprimir precios en Comandas"
        classes="btn-sm"
        checked={settings.price_ticket ? "true" : undefined}
        id="price_ticket"
      />
      <IconButton
        clickFun={updateSetting}
        text="Imprimir Tickets en Consola (Sporte Técnico)"
        classes="btn-sm"
        checked={settings.print_console ? "true" : undefined}
        id="print_console"
      />
    </div>
  </div>
);

const RightSide: React.FC = () => {
  const [settings, saveSettings] = useSettings();
  const [ticketTxt, setTicketTxt] = useState(settings.ticket_txt || "");
  const [logoUrl, setLogoUrl] = useState(settings.ticket_logo || "");
  const [preview, setPreview] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const maxLength = 150;

  const [uploadLogo, { loading: uploading }] = useMutation(
    TICKET_LOGO_UPLOAD,
    {
      onCompleted: (data) => {
        const url = data.ticketLogoUpload.url;
        setLogoUrl(url);
        saveSettings("ticket_logo", url);
        setPreview(null);
      },
      onError: (err) => {
        console.error("Error uploading ticket logo:", err);
        alert("Error al subir el logo. Intenta de nuevo.");
        setPreview(null);
      },
    }
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      setProcessing(true);
      try {
        const blob = await processImageForTicket(acceptedFiles[0]);
        // Show B&W preview
        const previewUrl = URL.createObjectURL(blob);
        setPreview(previewUrl);
        // Upload processed image
        const processedFile = new File([blob], "ticket_logo.png", {
          type: "image/png",
        });
        uploadLogo({ variables: { file: processedFile } });
      } catch (err) {
        console.error("Error processing image:", err);
        alert("Error al procesar la imagen.");
      }
      setProcessing(false);
    },
    [uploadLogo]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    accept: "image/png,image/jpeg,image/svg+xml",
    disabled: uploading || processing,
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0]?.errors[0];
      if (error?.code === "file-too-large") {
        alert("El archivo es demasiado grande. El tamaño máximo es 5MB.");
      } else if (error?.code === "file-invalid-type") {
        alert("Solo se permiten archivos PNG, JPG y SVG.");
      }
    },
  });

  const handleRemoveLogo = () => {
    setLogoUrl("");
    setPreview(null);
    saveSettings("ticket_logo", "");
  };

  const saveText = () => {
    saveSettings("ticket_txt", ticketTxt);
  };

  const handleText = (e: { target: HTMLTextAreaElement }) => {
    const newText = e.target.value;
    if (newText.length >= maxLength) return;
    setTicketTxt(newText);
  };

  const displayLogo = preview || logoUrl;
  const isLoading = uploading || processing;

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="">Personaliza tus Tickets</h3>
        <div className="form-group">
          <label>Logo en Ticket</label>
          <div
            {...getRootProps()}
            style={{
              border: "2px dashed #ccc",
              borderRadius: 8,
              padding: 20,
              textAlign: "center",
              cursor: isLoading ? "wait" : "pointer",
              backgroundColor: "#fafafa",
              minHeight: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <input {...getInputProps()} />
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin fa-2x mb-2"></i>
                <p className="mb-0 text-muted">
                  {processing ? "Procesando imagen..." : "Subiendo..."}
                </p>
              </>
            ) : displayLogo ? (
              <img
                src={displayLogo}
                alt="Logo ticket"
                style={{
                  maxWidth: 200,
                  maxHeight: 100,
                  imageRendering: "pixelated",
                }}
              />
            ) : (
              <>
                <i className="fas fa-cloud-upload-alt fa-2x mb-2 text-muted"></i>
                <p className="mb-0 text-muted">
                  Arrastra tu logo aquí o haz click para seleccionar
                </p>
              </>
            )}
          </div>
          <small className="form-text text-muted">
            La imagen se convertirá automáticamente a blanco y negro para
            impresión térmica. PNG o JPG, máx. 5MB.
          </small>
          {displayLogo && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger mt-2"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveLogo();
              }}
            >
              <i className="fas fa-trash mr-1"></i>Eliminar logo
            </button>
          )}
        </div>
        <div className="form-group">
          <label>Texto en Ticket</label>
          <textarea
            className="form-control"
            placeholder="Agrega un texto personalizado al final de tus tickets"
            id="ticket_info"
            rows={3}
            value={ticketTxt}
            onChange={handleText}
          />
          <small className="form-text text-muted">
            {`Quedan ${maxLength - ticketTxt.length} caracteres.`} Máximo{" "}
            {maxLength} caracteres.
          </small>
        </div>
        <button
          className="btn btn-success btn-block"
          type="button"
          onClick={saveText}
        >
          <i className="fas fa-scroll"></i> Guardar Texto
        </button>
      </div>
    </div>
  );
};

type PrinterFormState = {
  id?: string;
  ip_address: string;
  port: string;
};

const emptyPrinter: PrinterFormState = { ip_address: "", port: "9100" };

const NetworkPrinters: React.FC<{ branchId?: number }> = ({ branchId }) => {
  const { data, loading, refetch } = useQuery(GET_PRINTERS, {
    variables: { branch_id: branchId ? +branchId : undefined },
  });
  const [addPrinter] = useMutation(ADD_PRINTER);
  const [updatePrinter] = useMutation(UPDATE_PRINTER);
  const [deletePrinter] = useMutation(DELETE_PRINTER);

  const [form, setForm] = useState<PrinterFormState>(emptyPrinter);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const printers = data?.getPrinters || [];

  const handleEdit = (printer: any) => {
    setForm({
      id: printer.id,
      ip_address: printer.ip_address,
      port: printer.port,
    });
    setEditing(true);
  };

  const handleCancel = () => {
    setForm(emptyPrinter);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!form.ip_address || !form.port) return;
    setSaving(true);
    try {
      if (editing && form.id) {
        await updatePrinter({
          variables: {
            id: parseInt(form.id),
            ip_address: form.ip_address,
            port: form.port,
          },
        });
      } else {
        await addPrinter({
          variables: {
            branch_id: +(branchId || 0),
            ip_address: form.ip_address,
            port: form.port,
          },
        });
      }
      setForm(emptyPrinter);
      setEditing(false);
      await refetch();
    } catch (error) {
      console.error("Error saving printer:", error);
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Eliminar esta impresora?")) return;
    try {
      await deletePrinter({ variables: { id: parseInt(id) } });
      await refetch();
    } catch (error) {
      console.error("Error deleting printer:", error);
    }
  };

  return (
    <div className="card mt-3">
      <div className="card-body">
        <h5>
          <i className="fas fa-project-diagram mr-2"></i>
          Impresoras de Red
        </h5>
        {loading ? (
          <div className="text-center py-3">
            <i className="fas fa-spinner fa-spin"></i> Cargando...
          </div>
        ) : (
          <>
            {printers.map((printer: any) => (
              <div
                key={printer.id}
                className="d-flex align-items-center justify-content-between border-bottom py-2"
              >
                <div>
                  <i className="fas fa-print mr-2 text-muted"></i>
                  <strong>{printer.ip_address}</strong>
                  <span className="text-muted ml-2">:{printer.port}</span>
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mr-1"
                    onClick={() => handleEdit(printer)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(printer.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
            {printers.length === 0 && (
              <p className="text-muted small mb-2">
                No hay impresoras de red configuradas.
              </p>
            )}
          </>
        )}
        <div className="form-row mt-3">
          <div className="col-6">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Dirección IP (ej. 192.168.1.100)"
              value={form.ip_address}
              onChange={(e) =>
                setForm({ ...form, ip_address: e.target.value })
              }
            />
          </div>
          <div className="col-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Puerto"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: e.target.value })}
            />
          </div>
          <div className="col-3">
            <button
              type="button"
              className="btn btn-sm btn-success btn-block"
              onClick={handleSave}
              disabled={saving || !form.ip_address}
            >
              {saving ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : editing ? (
                <>
                  <i className="fas fa-save"></i> Guardar
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i> Agregar
                </>
              )}
            </button>
          </div>
        </div>
        {editing && (
          <button
            type="button"
            className="btn btn-sm btn-link text-muted mt-1"
            onClick={handleCancel}
          >
            Cancelar edición
          </button>
        )}
      </div>
    </div>
  );
};

export default Tickets;
